import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import { useParams, useLocation } from "react-router-dom";
import Navbar from "../../components/account/Navbar";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AddService from "./Add-Services";
import "../../styles/dashboard.css";
import "../../pages/reception/ReceptionStyles.css";

const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:8000";

const Services = forwardRef(({ clinicData, onAddService, clinicId = null }, ref) => {
  const location = useLocation();
  const { t, i18n } = useTranslation();
  // Read tab from query param
  const queryTab = React.useMemo(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get("tab");
    if (tab === "nonmedical") return "nonmedical";
    if (tab === "medication") return "medication";
    return "medical";
  }, [location.search]);
  const [activeTab, setActiveTab] = useState(queryTab);
  const [medicalServices, setMedicalServices] = useState([]);
  const [nonMedicalServices, setNonMedicalServices] = useState([]);
  const [medicationServices, setMedicationServices] = useState([]);

  // Filter states
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [selectedUnitFilter, setSelectedUnitFilter] = useState("all");

  // Sort states
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [selectedSort, setSelectedSort] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc"); // asc or desc
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteAllModalOpen, setDeleteAllModalOpen] = useState(false);
  const [serviceToDelete, setServiceToDelete] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showEditServiceDrawer, setShowEditServiceDrawer] = useState(false);
  const [serviceToEdit, setServiceToEdit] = useState(null);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [totalServices, setTotalServices] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");

  const navigate = useNavigate();
  const { clinic_id } = useParams();

  // Get clinic ID from prop, params, or centralized data
  const currentClinicId = clinicId || clinic_id || clinicData?.clinic?.id;

  const sortFieldLabel = t(`manageServices.actions.sort.fields.${selectedSort}`);
  const sortButtonLabel = `${t("manageServices.actions.sort.button")} (${sortFieldLabel} ${sortOrder === "asc" ? t("manageServices.actions.sort.icons.asc") : t("manageServices.actions.sort.icons.desc")
    })`;
  const activeUnitLabel =
    selectedUnitFilter === "all"
      ? ""
      : t(`manageServices.actions.filters.units.${selectedUnitFilter}`, {
        defaultValue: selectedUnitFilter,
      });

  const getServiceName = (service) => {
    if (!service) return "";
    if (i18n.language === "ar") {
      return service.service_name_ar || service.service_name_en || "";
    }
    return service.service_name_en || service.service_name_ar || "";
  };

  const getUnitLabel = (unit) =>
    unit
      ? t(`manageServices.units.${unit}`, { defaultValue: unit })
      : t("manageServices.units.unknown");

  // Update activeTab if query param changes
  useEffect(() => {
    setActiveTab(queryTab);
  }, [queryTab]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
    fetchServices(1, tab);
  };

  // Extract fetchServices function to be reusable
  const fetchServices = async (page = 1, forceTab = null, forceSearch = null) => {
    try {
      setIsLoading(true);
      const targetTab = forceTab || activeTab;
      const targetSearch = forceSearch !== null ? forceSearch : searchTerm;
      
      const token = localStorage.getItem("access_token");
      const apiServiceType = targetTab === "nonmedical" ? "non_medical" : targetTab;
      
      // Map frontend sort names to backend column names
      let apiSortBy = selectedSort;
      if (selectedSort === 'name') apiSortBy = 'service_name_en';
      if (selectedSort === 'status') apiSortBy = 'is_active';

      let url = `${API_BASE_URL}/api/v1/clinic-services?simple=true&clinic_id=${currentClinicId}`;
      url += `&page=${page}&per_page=5&service_type=${apiServiceType}`;
      url += `&sort_by=${apiSortBy}&sort_order=${sortOrder}`;
      
      if (selectedUnitFilter !== 'all') {
        url += `&unit=${selectedUnitFilter}`;
      }

      if (targetSearch) {
        url += `&search=${encodeURIComponent(targetSearch)}`;
      }

      const response = await fetch(url, {
        headers: {
          Accept: "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      
      const result = await response.json();
      
      if (result.data && Array.isArray(result.data.services)) {
        const newServices = result.data.services;
        const pagination = result.data.pagination;

        if (targetTab === "medical") setMedicalServices(newServices);
        else if (targetTab === "nonmedical") setNonMedicalServices(newServices);
        else if (targetTab === "medication") setMedicationServices(newServices);

        if (pagination) {
          setCurrentPage(pagination.current_page);
          setHasMore(pagination.current_page < pagination.last_page);
          setTotalServices(pagination.total);
          window.totalPages = pagination.last_page;
        } else {
          setHasMore(false);
        }
      }
    } catch (err) {
      console.error("Error fetching services:", err);
    } finally {
      setIsLoading(false);
    }
  };

  // Refresh on sort or filter change
  useEffect(() => {
    setCurrentPage(1);
    fetchServices(1);
  }, [selectedSort, sortOrder, selectedUnitFilter]);

  // Debounced search effect
  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
      fetchServices(1, activeTab, searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && (!window.totalPages || newPage <= window.totalPages)) {
      fetchServices(newPage);
    }
  };
  // Expose refreshServices method to parent component
  useImperativeHandle(ref, () => ({
    refreshServices: () => fetchServices(1, false)
  }));

  useEffect(() => {
    if (currentClinicId) {
      fetchServices(1, false);
    }
  }, [currentClinicId]);

  // Helper function to sort services
  const sortServices = (services) => {
    return [...services].sort((a, b) => {
      let aValue, bValue;

      switch (selectedSort) {
        case 'name':
          aValue = (a.service_name_en || '').toLowerCase();
          bValue = (b.service_name_en || '').toLowerCase();
          break;
        case 'price':
          aValue = parseFloat(a.final_price || a.price || 0);
          bValue = parseFloat(b.final_price || b.price || 0);
          break;
        case 'status':
          aValue = a.is_active ? 'active' : 'inactive';
          bValue = b.is_active ? 'active' : 'inactive';
          break;
        default:
          return 0;
      }

      if (selectedSort === 'price') {
        return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
      } else {
        if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
        return 0;
      }
    });
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showFilterDropdown && !event.target.closest('.services-filters-btn')) {
        setShowFilterDropdown(false);
      }
      if (showSortDropdown && !event.target.closest('.services-filters-btn')) {
        setShowSortDropdown(false);
      }
    };

    if (showFilterDropdown || showSortDropdown) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showFilterDropdown, showSortDropdown]);

  const handleDeleteService = async () => {
    if (!serviceToDelete) return;
    const token = localStorage.getItem("access_token");
    try {
      setIsLoading(true);
      const res = await fetch(
        `${API_BASE_URL}/api/v1/clinic-services/${serviceToDelete}`,
        {
          method: "DELETE",
          headers: {
            Accept: "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );
      if (!res.ok) {
        const text = await res.text();
        setDeleteModalOpen(false);
        alert(
          text ||
          t("manageServices.errors.deleteFailed", {
            defaultValue: "Failed to delete service",
          })
        );
        return;
      }
      setDeleteModalOpen(false);
      window.location.reload();
    } catch (err) {
      setDeleteModalOpen(false);
      alert(
        t("manageServices.errors.deleteError", {
          message: err.message,
          defaultValue: `Error deleting service: ${err.message}`,
        })
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteAllServices = async () => {
    const token = localStorage.getItem("access_token");
    const servicesToDelete =
      activeTab === "medical" ? medicalServices : nonMedicalServices;
    try {
      setIsLoading(true);
      for (const service of servicesToDelete) {
        const res = await fetch(
          `${API_BASE_URL}/api/v1/clinic-services/${service.id}`,
          {
            method: "DELETE",
            headers: {
              Accept: "application/json",
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
          }
        );
        if (!res.ok) {
          const text = await res.text();
          alert(
            text ||
            t("manageServices.errors.deleteFailedSpecific", {
              service: getServiceName(service),
              defaultValue: `Failed to delete service ${getServiceName(
                service
              )}`,
            })
          );
          setDeleteAllModalOpen(false);
          return;
        }
      }
      setDeleteAllModalOpen(false);
      window.location.reload();
    } catch (err) {
      setDeleteAllModalOpen(false);
      alert(
        t("manageServices.errors.deleteAllError", {
          message: err.message,
          defaultValue: `Error deleting all services: ${err.message}`,
        })
      );
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <div
      className="clinicinfo-member-wrapper "
      dir={i18n.dir()}
      lang={i18n.language}
    >
      <Navbar />
      <div className="reception-backbar" dir={i18n.dir()} lang={i18n.language}>
        <button
          type="button"
          onClick={() => navigate(`/medical/clinic/${currentClinicId}/setup`)}
          className="reception-backbtn"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '8px' }}>
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          {t("reception.backBar.backToOperations")}
        </button>
      </div>
      <div
        className="services-container hide-scrollbar"
        style={{
          minHeight: "calc(100vh - 100px)",
          overflowY: "auto",
          overscrollBehavior: "contain",
          padding: "0 32px 32px 32px",
        }}
      >
        <div className="services-header-row">
          <h2 className="services-title">{t("manageServices.title")}</h2>
        </div>
        <div
          className="services-tabs"
          dir={i18n.dir()}
          lang={i18n.language}
        >
          <button
            className={`services-tab${activeTab === "medical" ? " active" : ""}`}
            style={activeTab === "medical" ? { background: "rgba(0,212,179,0.12)", color: "#0f766e" } : {}}
            onClick={() => handleTabChange("medical")}
            disabled={isLoading}
          >
            {t("manageServices.tabs.medical")}
          </button>
          <button
            className={`services-tab${activeTab === "nonmedical" ? " active" : ""}`}
            style={activeTab === "nonmedical" ? { background: "rgba(0,212,179,0.12)", color: "#0f766e" } : {}}
            onClick={() => handleTabChange("nonmedical")}
            disabled={isLoading}
          >
            {t("manageServices.tabs.nonMedical")}
          </button>
          <button
            className={`services-tab${activeTab === "medication" ? " active" : ""}`}
            style={activeTab === "medication" ? { background: "rgba(0,212,179,0.12)", color: "#0f766e" } : {}}
            onClick={() => handleTabChange("medication")}
            disabled={isLoading}
          >
            {t("manageServices.tabs.medication")}
          </button>
        </div>
        <div className="services-table-actions" style={{ justifyContent: 'space-between', display: 'flex', alignItems: 'center' }}>
          <div className="services-search-container" style={{ position: 'relative', width: '300px' }}>
            <input
              type="text"
              placeholder={t("manageServices.common.searchPlaceholder", { defaultValue: "Search service name..." })}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="services-search-input"
              style={{
                width: '100%',
                padding: i18n.dir() === 'rtl' ? '10px 40px 10px 16px' : '10px 16px 10px 40px',
                borderRadius: '10px',
                border: '1.5px solid #e5e7eb',
                fontSize: '14px',
                outline: 'none',
                transition: 'all 0.3s ease',
                background: '#fff',
                textAlign: i18n.dir() === 'rtl' ? 'right' : 'left'
              }}
            />
            <span style={{ 
              position: 'absolute', 
              [i18n.dir() === 'rtl' ? 'right' : 'left']: '14px', 
              top: '50%', 
              transform: 'translateY(-50%)', 
              color: '#9ca3af' 
            }}>
              🔍
            </span>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ position: "relative" }}>
            <button
              className="services-filters-btn"
              disabled={isLoading}
              onClick={() => setShowSortDropdown(!showSortDropdown)}
              style={{ marginRight: "8px" }}
            >
              {sortButtonLabel}
              <span style={{ marginLeft: "4px" }}>
                {showSortDropdown
                  ? t("manageServices.common.caret.up")
                  : t("manageServices.common.caret.down")}
              </span>
            </button>

            {showSortDropdown && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                backgroundColor: '#fff',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                zIndex: 10,
                minWidth: '160px',
                marginTop: '4px'
              }}>
                <div style={{ padding: '8px 0' }}>
                  <div style={{ padding: '8px 16px', fontSize: '12px', fontWeight: '600', color: '#6b7280', borderBottom: '1px solid #f3f4f6' }}>
                    {t("manageServices.actions.sort.menuTitle")}
                  </div>
                  <button
                    onClick={() => {
                      setSelectedSort('name');
                      setSortOrder(selectedSort === 'name' && sortOrder === 'asc' ? 'desc' : 'asc');
                      setShowSortDropdown(false);
                    }}
                    style={{
                      width: '100%',
                      padding: '8px 16px',
                      border: 'none',
                      background: selectedSort === 'name' ? '#f3f4f6' : 'transparent',
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontSize: '14px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    {t("manageServices.actions.sort.fields.name")}
                    {selectedSort === "name" && (
                      <span>
                        {sortOrder === "asc"
                          ? t("manageServices.actions.sort.icons.asc")
                          : t("manageServices.actions.sort.icons.desc")}
                      </span>
                    )}
                  </button>
                  <button
                    onClick={() => {
                      setSelectedSort('price');
                      setSortOrder(selectedSort === 'price' && sortOrder === 'asc' ? 'desc' : 'asc');
                      setShowSortDropdown(false);
                    }}
                    style={{
                      width: '100%',
                      padding: '8px 16px',
                      border: 'none',
                      background: selectedSort === 'price' ? '#f3f4f6' : 'transparent',
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontSize: '14px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    {t("manageServices.actions.sort.fields.price")}
                    {selectedSort === "price" && (
                      <span>
                        {sortOrder === "asc"
                          ? t("manageServices.actions.sort.icons.asc")
                          : t("manageServices.actions.sort.icons.desc")}
                      </span>
                    )}
                  </button>
                  <button
                    onClick={() => {
                      setSelectedSort('status');
                      setSortOrder(selectedSort === 'status' && sortOrder === 'asc' ? 'desc' : 'asc');
                      setShowSortDropdown(false);
                    }}
                    style={{
                      width: '100%',
                      padding: '8px 16px',
                      border: 'none',
                      background: selectedSort === 'status' ? '#f3f4f6' : 'transparent',
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontSize: '14px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    {t("manageServices.actions.sort.fields.status")}
                    {selectedSort === "status" && (
                      <span>
                        {sortOrder === "asc"
                          ? t("manageServices.actions.sort.icons.asc")
                          : t("manageServices.actions.sort.icons.desc")}
                      </span>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
          <div style={{ position: "relative" }}>
            <button
              className="services-filters-btn"
              disabled={isLoading}
              onClick={() => setShowFilterDropdown(!showFilterDropdown)}
            >
              {t("manageServices.actions.filters.button")}
              {selectedUnitFilter !== "all" && ` (${activeUnitLabel})`}
              <span style={{ marginLeft: "4px" }}>
                {showFilterDropdown
                  ? t("manageServices.common.caret.up")
                  : t("manageServices.common.caret.down")}
              </span>
            </button>

            {showFilterDropdown && (
              <div
                style={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  backgroundColor: "#fff",
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                  zIndex: 10,
                  minWidth: "150px",
                  marginTop: "4px",
                }}
              >
                <div style={{ padding: "8px 0" }}>
                  <button
                    onClick={() => {
                      setSelectedUnitFilter("all");
                      setShowFilterDropdown(false);
                    }}
                    style={{
                      width: "100%",
                      padding: "8px 16px",
                      border: "none",
                      background:
                        selectedUnitFilter === "all" ? "#f3f4f6" : "transparent",
                      textAlign: "left",
                      cursor: "pointer",
                      fontSize: "14px",
                    }}
                  >
                    {t("manageServices.actions.filters.units.all")}
                  </button>
                  <button
                    onClick={() => {
                      setSelectedUnitFilter("pieces");
                      setShowFilterDropdown(false);
                    }}
                    style={{
                      width: "100%",
                      padding: "8px 16px",
                      border: "none",
                      background:
                        selectedUnitFilter === "pieces"
                          ? "#f3f4f6"
                          : "transparent",
                      textAlign: "left",
                      cursor: "pointer",
                      fontSize: "14px",
                    }}
                  >
                    {t("manageServices.actions.filters.units.pieces")}
                  </button>
                  <button
                    onClick={() => {
                      setSelectedUnitFilter("session");
                      setShowFilterDropdown(false);
                    }}
                    style={{
                      width: "100%",
                      padding: "8px 16px",
                      border: "none",
                      background:
                        selectedUnitFilter === "session"
                          ? "#f3f4f6"
                          : "transparent",
                      textAlign: "left",
                      cursor: "pointer",
                      fontSize: "14px",
                    }}
                  >
                    {t("manageServices.actions.filters.units.session")}
                  </button>
                  <button
                    onClick={() => {
                      setSelectedUnitFilter("other");
                      setShowFilterDropdown(false);
                    }}
                    style={{
                      width: "100%",
                      padding: "8px 16px",
                      border: "none",
                      background:
                        selectedUnitFilter === "other"
                          ? "#f3f4f6"
                          : "transparent",
                      textAlign: "left",
                      cursor: "pointer",
                      fontSize: "14px",
                    }}
                  >
                    {t("manageServices.actions.filters.units.other")}
                  </button>
                </div>
              </div>
            )}
          </div>
          <button
            className="services-delete-btn"
            onClick={() => setDeleteAllModalOpen(true)}
            disabled={isLoading}
          >
            {t("manageServices.actions.deleteAll")}
          </button>
          <button
            className="services-add-btn"
            style={{ background: "#00D4B3", color: "#0b2e2a" }}
            onClick={onAddService}
            disabled={isLoading}
          >
            {t("manageServices.actions.addNew")}
          </button>
        </div>
      </div>
        {isLoading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '60px 20px', minHeight: '200px' }}>
            <div className="loading-spinner"></div>
          </div>
        ) : (
          <table className="services-table">
            <thead>
              <tr>
                <th>{t("manageServices.table.headers.code")}</th>
                <th>{t("manageServices.table.headers.name")}</th>
                <th>{t("manageServices.table.headers.unit")}</th>
                <th>{t("manageServices.table.headers.vat")}</th>
                <th>{t("manageServices.table.headers.price")}</th>
                <th>{t("manageServices.table.headers.status")}</th>
                <th>{t("manageServices.table.headers.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                const services =
                  activeTab === "medical"
                    ? medicalServices
                    : activeTab === "nonmedical"
                      ? nonMedicalServices
                      : medicationServices;

                if (services.length === 0) {
                  return (
                    <tr>
                      <td colSpan={7} style={{ textAlign: "center", color: "#888", padding: '40px' }}>
                        {selectedUnitFilter === "all"
                          ? t("manageServices.table.emptyAll")
                          : t("manageServices.table.emptyFiltered", {
                            unit: activeUnitLabel,
                          })}
                      </td>
                    </tr>
                  );
                }

                return services.map((service) => (
                  <tr key={service.id}>
                    <td>{service.service_code}</td>
                    <td>{getServiceName(service)}</td>
                    <td>{getUnitLabel(service.unit)}</td>
                    <td>
                      {service.vat_percentage
                        ? `${service.vat_percentage}%`
                        : t("manageServices.vat.none")}
                    </td>
                    <td>
                      {service.final_price ||
                        service.price ||
                        t("manageServices.price.unset")}
                    </td>
                    <td>
                      <span
                        className={`services-status-${service.is_active ? "active" : "inactive"}`}
                        style={
                          service.is_active
                            ? {
                              background: "rgba(0,212,179,0.15)",
                              color: "#065f46",
                              border: "1px solid #a7f3d0",
                              padding: "6px 10px",
                              borderRadius: "9999px",
                              fontWeight: 600,
                            }
                            : undefined
                        }
                      >
                        ●{" "}
                        {t(
                          `manageServices.table.status.${service.is_active ? "active" : "inactive"}`
                        )}
                      </span>
                    </td>
                    <td>
                      <button
                        className="services-action-edit"
                        title={t("manageServices.actionsColumn.edit")}
                        onClick={() => {
                          setServiceToEdit(service);
                          setShowEditServiceDrawer(true);
                        }}
                        aria-label={t("manageServices.actionsColumn.edit")}
                      >
                        ✏️
                      </button>
                      <button
                        className="services-action-delete"
                        title={t("manageServices.actionsColumn.delete")}
                        onClick={() => {
                          setServiceToDelete(service.id);
                          setDeleteModalOpen(true);
                        }}
                        aria-label={t("manageServices.actionsColumn.delete")}
                      >
                        🗑️
                      </button>
                    </td>
                  </tr>
                ));
              })()}
            </tbody>
          </table>
        )}

        {totalServices > 0 && (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            marginTop: '32px', 
            marginBottom: '32px',
            gap: '8px'
          }}>
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1 || isLoading}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
                background: 'white',
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                opacity: currentPage === 1 ? 0.5 : 1,
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              {t("manageServices.pagination.previous", { defaultValue: "Previous" })}
            </button>
            
            <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
              <span style={{ 
                padding: '8px 16px', 
                borderRadius: '8px', 
                background: '#00D4B3', 
                color: '#0b2e2a',
                fontWeight: '600',
                fontSize: '14px'
              }}>
                {currentPage}
              </span>
              {window.totalPages && (
                <span style={{ color: '#6b7280', fontSize: '14px' }}>
                  {t("manageServices.pagination.of", { defaultValue: "of" })} {window.totalPages}
                </span>
              )}
            </div>

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={!hasMore || isLoading}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
                background: 'white',
                cursor: !hasMore ? 'not-allowed' : 'pointer',
                opacity: !hasMore ? 0.5 : 1,
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              {t("manageServices.pagination.next", { defaultValue: "Next" })}
            </button>
          </div>
        )}
      </div>
      {deleteModalOpen && (
        <div className="modal-overlay" dir={i18n.dir()} lang={i18n.language}>
          <div className="modal-content">
            <h3>{t("manageServices.modals.delete.title")}</h3>
            <p>{t("manageServices.modals.delete.message")}</p>
            <div style={{ display: "flex", gap: "12px", marginTop: "18px" }}>
              <button
                className="modal-btn modal-btn-danger"
                onClick={handleDeleteService}
              >
                {t("manageServices.modals.delete.confirm")}
              </button>
              <button
                className="modal-btn"
                onClick={() => setDeleteModalOpen(false)}
              >
                {t("manageServices.modals.delete.cancel")}
              </button>
            </div>
          </div>
        </div>
      )}
      {deleteAllModalOpen && (
        <div className="modal-overlay" dir={i18n.dir()} lang={i18n.language}>
          <div className="modal-content">
            <h3>{t("manageServices.modals.deleteAll.title")}</h3>
            <p>{t("manageServices.modals.deleteAll.message")}</p>
            <div style={{ display: "flex", gap: "12px", marginTop: "18px" }}>
              <button
                className="modal-btn modal-btn-danger"
                onClick={handleDeleteAllServices}
              >
                {t("manageServices.modals.deleteAll.confirm")}
              </button>
              <button
                className="modal-btn"
                onClick={() => setDeleteAllModalOpen(false)}
              >
                {t("manageServices.modals.deleteAll.cancel")}
              </button>
            </div>
          </div>
        </div>
      )}
      <style>
        {`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100vw;
          height: 100vh;
          background: rgba(0, 0, 0, 0.6);
          backdrop-filter: blur(12px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          animation: fadeIn 0.3s ease;
        }
        .modal-content {
          background: #fff;
          padding: 32px 28px 28px 28px;
          border-radius: 16px;
          box-shadow: 0 24px 48px -12px rgba(0, 0, 0, 0.25);
          width: 360px;
          max-width: 90vw;
          text-align: center;
          font-family: '"Red Hat Display", sans-serif;
          position: relative;
          animation: slideUp 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        }
        .modal-content::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(90deg, #ef4444, #dc2626);
          border-radius: 20px 20px 0 0;
        }
        .modal-content h3 {
          font-size: 22px;
          font-weight: 700;
          color: #1f2937;
          margin: 0 0 12px 0;
          letter-spacing: -0.025em;
        }
        .modal-content p {
          font-size: 15px;
          color: #6b7280;
          line-height: 1.6;
          margin: 0 0 32px 0;
        }
        .modal-btn {
          padding: 12px 24px;
          border-radius: 10px;
          border: none;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          font-family: inherit;
          transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
          position: relative;
          overflow: hidden;
          min-width: 90px;
        }
        .modal-btn:not(.modal-btn-danger) {
          background: #f8fafc;
          color: #64748b;
          border: 1.5px solid #e2e8f0;
        }
        .modal-btn-danger {
          background: linear-gradient(135deg, #ef4444, #dc2626);
          color: #fff;
          box-shadow: 0 4px 16px rgba(239, 68, 68, 0.3);
        }
        .modal-btn:hover:not(.modal-btn-danger) {
          background: #f1f5f9;
          border-color: #cbd5e1;
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
        }
        .modal-btn-danger:hover {
          background: linear-gradient(135deg, #dc2626, #b91c1c);
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(239, 68, 68, 0.4);
        }
        .modal-btn:active {
          transform: translateY(0);
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
      `}
      </style>

      {/* Edit Service Modal */}
      {showEditServiceDrawer && (
        <div
          className="services-modal-overlay"
          onClick={() => {
            setShowEditServiceDrawer(false);
            setServiceToEdit(null);
          }}
        >
          <div
            className="services-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <AddService
              editServiceData={serviceToEdit}
              onClose={() => {
                setShowEditServiceDrawer(false);
                setServiceToEdit(null);
              }}
              onServiceAdded={() => {
                fetchServices(1, false);
                setShowEditServiceDrawer(false);
                setServiceToEdit(null);
              }}
              clinicId={clinicId}
            />
          </div>
        </div>
      )}
    </div>
  );
});

export default Services;
